import { memoize } from 'micro-memoize';

import { Organisation } from '@futrli-sdk/organisations';
import { Role } from '@futrli-sdk/roles';
import { WorkspaceMember, WorkspaceMemberRole } from '@futrli-sdk/people';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

import {
  addOrganisationsToMemberMutation,
  createInvitationMutation,
  deleteMembersMutation,
  deleteOrganisationFromMemberMutation,
  resendInviteMutation,
  updateMemberRoleMutation
} from '../../../mutations/members';
import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { currentUserQuery } from '../../../queries/currentUser';
import {
  getEligibleUserOrganisations,
  getInviteUserPayload,
  getModifyUserChanges,
  UserFormValues
} from '../../../processors/users/userForm';
import { getUserName } from '../../../utils/users/getUserName';
import { organisationsQuery } from '../../../queries/organisations';
import { ProcessedUserRow, processUserRows } from '../../../processors/users/usersList';
import { rolesQuery } from '../../../queries/roles';
import { USER_ROLE_IDS } from '../../../processors/users/constants';
import { WorkspaceMemberOrganisationPatch } from '../../../../predict/src/core/types';
import { workspaceMembersQuery } from '../../../queries/members';
import { workspacesListingQuery } from '../../../operations/workspaces/queries/workspacesListingQuery';

export interface UsersViewCurrentUser {
  id: string;
  username: string;
}

export interface UsersViewState extends ApiViewState {
  currentUser?: UsersViewCurrentUser;
  errorAddingOrganisations: Error | null;
  errorCreatingInvitation: Error | null;
  errorDeletingOrganisation: Error | null;
  errorDeletingUsers: Error | null;
  errorResendingInvitation: Error | null;
  errorUpdatingRole: Error | null;
  eligibleOrganisations: Organisation[];
  isAddingOrganisations: boolean;
  isCreatingInvitation: boolean;
  isDeletingOrganisation: boolean;
  isDeletingUsers: boolean;
  isResendingInvitation: boolean;
  isUpdatingRole: boolean;
  organisations: Organisation[];
  roles: Role[];
  users: ProcessedUserRow[];
}

export class UsersView extends ApiView {
  private addOrganisationsMut: ReturnType<typeof addOrganisationsToMemberMutation>;
  private createInvitationMut: ReturnType<typeof createInvitationMutation>;
  private deleteMembersMut: ReturnType<typeof deleteMembersMutation>;
  private deleteOrganisationMut: ReturnType<typeof deleteOrganisationFromMemberMutation>;
  private resendInviteMut: ReturnType<typeof resendInviteMutation>;
  private updateMemberRoleMut: ReturnType<typeof updateMemberRoleMutation>;

  private currentUserObs: ReturnType<typeof currentUserQuery>;
  private organisationsObs: ReturnType<typeof organisationsQuery>;
  private rolesObs: ReturnType<typeof rolesQuery>;
  private usersObs: ReturnType<typeof workspaceMembersQuery>;
  private workspacesObs: ReturnType<typeof workspacesListingQuery>;

  private memoizedProcessUserRows: typeof processUserRows;
  private memoizedGetEligibleUserOrganisations: typeof getEligibleUserOrganisations;

  public state: UsersViewState = {
    ...getDefaultApiViewState(),
    currentUser: undefined,
    eligibleOrganisations: [],
    errorAddingOrganisations: null,
    errorCreatingInvitation: null,
    errorDeletingOrganisation: null,
    errorDeletingUsers: null,
    errorResendingInvitation: null,
    errorUpdatingRole: null,
    isAddingOrganisations: false,
    isCreatingInvitation: false,
    isDeletingOrganisation: false,
    isDeletingUsers: false,
    isResendingInvitation: false,
    isUpdatingRole: false,
    organisations: [],
    roles: [],
    users: []
  };

  constructor(private workspaceId: string) {
    super();

    this.currentUserObs = this.addQuery(currentUserQuery());
    this.organisationsObs = this.addQuery(organisationsQuery(workspaceId));
    this.rolesObs = this.addQuery(rolesQuery(workspaceId));
    this.usersObs = this.addQuery(workspaceMembersQuery(workspaceId));
    this.workspacesObs = this.addQuery(workspacesListingQuery());

    this.memoizedProcessUserRows = memoize(processUserRows, { isKeyItemEqual: 'shallow' });
    this.memoizedGetEligibleUserOrganisations = memoize(getEligibleUserOrganisations);

    this.addOrganisationsMut = this.addMutation(addOrganisationsToMemberMutation(workspaceId));
    this.createInvitationMut = this.addMutation(createInvitationMutation(workspaceId));
    this.deleteMembersMut = this.addMutation(deleteMembersMutation(workspaceId));
    this.deleteOrganisationMut = this.addMutation(deleteOrganisationFromMemberMutation(workspaceId));
    this.resendInviteMut = this.addMutation(resendInviteMutation(workspaceId));
    this.updateMemberRoleMut = this.addMutation(updateMemberRoleMutation(workspaceId));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public addOrganisationsToMember(memberId: string, organisations: WorkspaceMemberOrganisationPatch[]) {
    return this.addOrganisationsMut.mutate({ memberId, organisations });
  }

  public deleteMembers(memberIds: string[]) {
    return this.deleteMembersMut.mutate(memberIds);
  }

  public deleteOrganisationFromMember(memberId: string, organisationId: string) {
    return this.deleteOrganisationMut.mutate({ memberId, organisationId });
  }

  public inviteUser(values: UserFormValues) {
    return this.createInvitationMut.mutate([getInviteUserPayload(values, this.eligibleOrganisations)]);
  }

  public async modifyUser(user: ProcessedUserRow, values: UserFormValues) {
    const changes = getModifyUserChanges(values, user, this.eligibleOrganisations);

    if (user.permissions.canEditRole && changes.roleId !== user.role.id) {
      await this.updateMemberRole(user.id, { id: changes.roleId });
    }

    if (!user.permissions.canChangeOrganisations || changes.roleId === USER_ROLE_IDS.SUPER_ADMIN) {
      return;
    }

    if (changes.addedOrganisations.length) {
      await this.addOrganisationsToMember(user.id, changes.addedOrganisations);
    }

    for (const organisationId of changes.removedOrganisationIds) {
      await this.deleteOrganisationFromMember(user.id, organisationId);
    }
  }

  public resendInvite(memberId: string) {
    return this.resendInviteMut.mutate(memberId);
  }

  public resetMutations() {
    this.addOrganisationsMut.reset();
    this.createInvitationMut.reset();
    this.deleteMembersMut.reset();
    this.deleteOrganisationMut.reset();
    this.resendInviteMut.reset();
    this.updateMemberRoleMut.reset();
    this.updateState();
  }

  public updateMemberRole(memberId: string, role: Partial<WorkspaceMemberRole>) {
    return this.updateMemberRoleMut.mutate({ memberId, role });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  private get currentUser(): UsersViewCurrentUser | undefined {
    const currentUser = this.getResult(this.currentUserObs).data;
    if (!currentUser) {
      return undefined;
    }
    return {
      id: currentUser.id,
      username: getUserName(currentUser.name)
    };
  }

  private get errorAddingOrganisations(): Error | null {
    return this.addOrganisationsMut.getCurrentResult().error;
  }

  private get errorCreatingInvitation(): Error | null {
    return this.createInvitationMut.getCurrentResult().error;
  }

  private get errorDeletingOrganisation(): Error | null {
    return this.deleteOrganisationMut.getCurrentResult().error;
  }

  private get errorDeletingUsers(): Error | null {
    return this.deleteMembersMut.getCurrentResult().error;
  }

  private get errorResendingInvitation(): Error | null {
    return this.resendInviteMut.getCurrentResult().error;
  }

  private get errorUpdatingRole(): Error | null {
    return this.updateMemberRoleMut.getCurrentResult().error;
  }

  protected getNewState(): UsersViewState {
    return {
      ...super.getNewState(),
      currentUser: this.currentUser,
      eligibleOrganisations: this.eligibleOrganisations,
      errorAddingOrganisations: this.errorAddingOrganisations,
      errorCreatingInvitation: this.errorCreatingInvitation,
      errorDeletingOrganisation: this.errorDeletingOrganisation,
      errorDeletingUsers: this.errorDeletingUsers,
      errorResendingInvitation: this.errorResendingInvitation,
      errorUpdatingRole: this.errorUpdatingRole,
      isAddingOrganisations: this.isAddingOrganisations,
      isCreatingInvitation: this.isCreatingInvitation,
      isDeletingOrganisation: this.isDeletingOrganisation,
      isDeletingUsers: this.isDeletingUsers,
      isResendingInvitation: this.isResendingInvitation,
      isUpdatingRole: this.isUpdatingRole,
      organisations: this.organisations,
      roles: this.roles,
      users: this.users
    };
  }

  private get isAddingOrganisations(): boolean {
    return this.addOrganisationsMut.getCurrentResult().status === 'pending';
  }

  private get isCreatingInvitation(): boolean {
    return this.createInvitationMut.getCurrentResult().status === 'pending';
  }

  private get isDeletingOrganisation(): boolean {
    return this.deleteOrganisationMut.getCurrentResult().status === 'pending';
  }

  private get isDeletingUsers(): boolean {
    return this.deleteMembersMut.getCurrentResult().status === 'pending';
  }

  private get isResendingInvitation(): boolean {
    return this.resendInviteMut.getCurrentResult().status === 'pending';
  }

  private get isUpdatingRole(): boolean {
    return this.updateMemberRoleMut.getCurrentResult().status === 'pending';
  }

  private get organisations(): Organisation[] {
    return this.getResult(this.organisationsObs).data ?? [];
  }

  private get eligibleOrganisations(): Organisation[] {
    return this.memoizedGetEligibleUserOrganisations(this.organisations);
  }

  private get roles(): Role[] {
    return this.getResult(this.rolesObs).data ?? [];
  }

  private get rawUsers(): WorkspaceMember[] {
    return this.getResult(this.usersObs).data ?? [];
  }

  private get users(): ProcessedUserRow[] {
    const currentUser = this.currentUser;
    const workspace = this.workspace;
    if (!currentUser || !workspace) {
      return [];
    }
    return this.memoizedProcessUserRows(this.rawUsers, currentUser, workspace);
  }

  private get workspace(): WorkspacesListingItem | undefined {
    return this.getResult(this.workspacesObs).data?.find(workspace => workspace.id === this.workspaceId);
  }
}
